import { useState } from "react";
import { Plug, CheckCircle2, Loader2, Unplug, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";

const PROVIDERS = {
  tripletex: { name: "Tripletex", field: "employee_token", label: "Employee Token", help: "Opprettes i Tripletex under Selskap → Vår API-tilgang → Ny nøkkel." },
  poweroffice: { name: "PowerOffice", field: "client_key", label: "Client Key", help: "Client Key opprettes når selskapet aktiverer integrasjonen din i PowerOffice Go." },
};

export function AccountingConnection({ companyId, companyName, source, provider, connected, dataLoaded, onReload }) {
  const [connectProvider, setConnectProvider] = useState(null);
  const [token, setToken] = useState("");
  const [env, setEnv] = useState("produksjon");
  const [connecting, setConnecting] = useState(false);

  const cp = connectProvider ? PROVIDERS[connectProvider] : null;

  const comingSoon = (name) => toast.info(`Integrasjon med ${name} kommer snart`, {
    description: "Da hentes nøkkeltallene automatisk fra regnskapet.",
  });

  const doConnect = async () => {
    if (!connectProvider) return;
    if (!token.trim()) { toast.error(`Lim inn en ${cp.label} fra ${cp.name}`); return; }
    setConnecting(true);
    try {
      await api.post(`/companies/${companyId}/${connectProvider}/connect`, { [cp.field]: token.trim(), env });
      toast.success(`Koblet til ${cp.name}`, { description: "Nøkkeltallene hentes nå fra regnskapet." });
      setConnectProvider(null);
      setToken("");
      onReload();
    } catch (e) {
      toast.error(`Kunne ikke koble til ${cp.name}`, { description: formatApiError(e?.response?.data?.detail) });
    } finally {
      setConnecting(false);
    }
  };

  const doDisconnect = async () => {
    const pv = (provider || "").toLowerCase();
    try {
      await api.delete(`/companies/${companyId}/${pv}`);
      toast.success(`Koblet fra ${provider}`, { description: "Viser demodata igjen." });
      onReload();
    } catch (e) {
      toast.error("Kunne ikke koble fra", { description: formatApiError(e?.response?.data?.detail) });
    }
  };

  return (
    <div className="flex flex-wrap items-center gap-2">
      <Button variant="ghost" size="sm" className="gap-2" data-testid="refresh-financials-btn" onClick={onReload} disabled={!dataLoaded}>
        <RefreshCw className={`h-4 w-4 ${!dataLoaded ? "animate-spin" : ""}`} /> {connected ? "Oppdater fra Tripletex" : "Oppdater"}
      </Button>
      {connected ? (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="sm" className="gap-2" data-testid="accounting-connected-btn">
              <CheckCircle2 className="h-4 w-4 text-emerald-500" /> {provider} tilkoblet
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <DropdownMenuLabel className="text-xs uppercase tracking-wider text-muted-foreground">
              Regnskapsintegrasjon
            </DropdownMenuLabel>
            <p className="px-2 py-1 text-[11px] text-muted-foreground">Miljø: {source.env || "produksjon"}</p>
            <DropdownMenuSeparator />
            <DropdownMenuItem data-testid="disconnect-tripletex" onClick={doDisconnect} className="text-destructive focus:text-destructive">
              <Unplug className="mr-2 h-4 w-4" /> Koble fra {provider}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      ) : (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="sm" className="gap-2" data-testid="connect-accounting-btn">
              <Plug className="h-4 w-4" /> Koble til regnskapssystem
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <DropdownMenuLabel className="text-xs uppercase tracking-wider text-muted-foreground">
              Regnskapssystemer
            </DropdownMenuLabel>
            <DropdownMenuItem data-testid="connect-tripletex" onClick={() => setConnectProvider("tripletex")}>
              <Plug className="mr-2 h-4 w-4 text-primary" /> Tripletex
            </DropdownMenuItem>
            <DropdownMenuItem data-testid="connect-poweroffice" onClick={() => setConnectProvider("poweroffice")}>
              <Plug className="mr-2 h-4 w-4 text-primary" /> PowerOffice
            </DropdownMenuItem>
            <DropdownMenuItem data-testid="connect-fiken" onClick={() => comingSoon("Fiken")}>
              <Plug className="mr-2 h-4 w-4 text-muted-foreground" /> Fiken
              <span className="ml-auto text-[10px] text-muted-foreground">snart</span>
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <p className="px-2 py-1.5 text-[11px] text-muted-foreground">Tallene under er eksempeldata.</p>
          </DropdownMenuContent>
        </DropdownMenu>
      )}

      <Dialog open={!!connectProvider} onOpenChange={(o) => !o && setConnectProvider(null)}>
        <DialogContent data-testid={cp ? `${connectProvider}-connect-dialog` : undefined}>
          <DialogHeader>
            <DialogTitle>Koble til {cp?.name}</DialogTitle>
            <DialogDescription>
              Lim inn selskapets <strong>{cp?.label}</strong> fra {cp?.name}. Da hentes nøkkeltallene
              automatisk fra regnskapet for {companyName}. Nøkkelen lagres kryptert.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-1">
            <div className="space-y-1.5">
              <Label htmlFor="acc-token">{cp?.label}</Label>
              <Input
                id="acc-token" data-testid={cp ? `${connectProvider}-token-input` : undefined} type="password"
                autoComplete="off" placeholder="lim inn API-nøkkel"
                value={token} onChange={(e) => setToken(e.target.value)}
              />
              <p className="text-[11px] text-muted-foreground">{cp?.help}</p>
            </div>
            <div className="space-y-1.5">
              <Label>Miljø</Label>
              <RadioGroup value={env} onValueChange={setEnv} className="flex gap-6" data-testid={cp ? `${connectProvider}-env` : undefined}>
                <label className="flex cursor-pointer items-center gap-2 text-sm">
                  <RadioGroupItem value="produksjon" data-testid="env-produksjon" /> Produksjon
                </label>
                <label className="flex cursor-pointer items-center gap-2 text-sm">
                  <RadioGroupItem value="test" data-testid="env-test" /> {connectProvider === "poweroffice" ? "Demo" : "Test"}
                </label>
              </RadioGroup>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setConnectProvider(null)} data-testid={cp ? `${connectProvider}-cancel` : undefined}>Avbryt</Button>
            <Button onClick={doConnect} disabled={connecting} data-testid={cp ? `${connectProvider}-connect-submit` : undefined} className="gap-2">
              {connecting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plug className="h-4 w-4" />}
              Koble til
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
